// Sentry must initialize before any other app code — keep this import first.
import './instrument';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { reactErrorHandler } from '@sentry/react';
import { RouterProvider } from 'react-router/dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { router } from './router';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ToastProvider } from './components/ui/Toast';
import { queryClient } from './lib/queryClient';
import { UpdatePrompt } from './components/layout/UpdatePrompt';
import { initPostHog } from './lib/posthog';
import './index.css';

initPostHog();

// Amplify is configured lazily — not here. A logged-out visitor (landing page)
// never loads the SDK; AuthContext configures it when a stored session exists,
// and the auth/api paths await ensureAmplifyConfigured() before using Amplify.

// Service worker registration is handled by useServiceWorker hook in UpdatePrompt

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

createRoot(rootElement, {
  // Route React 19 render errors to Sentry (uncaught, caught, and recoverable).
  onUncaughtError: reactErrorHandler(),
  onCaughtError: reactErrorHandler(),
  onRecoverableError: reactErrorHandler(),
}).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <NotificationProvider>
          <ToastProvider>
            <RouterProvider router={router} />
            <UpdatePrompt />
          </ToastProvider>
        </NotificationProvider>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>
);
